<template>
  <div class="mb-5">
    <div class="pt-4 pr-4 pb-0 d-flex justify-content-center align-items-center">
      <div class="mx-4">
        <h5 class="mb-0 mr-50">
          To Do: {{ incompleteCount }}
        </h5>
        <h5 class="mb-0 mr-50">
          Completed Today: {{ completedTodayCount }}
        </h5>
      </div>
      <validation-provider
        name="number-of-operators"
        rules="required|min_value:0"
        tag="div"
        v-slot="v"
        class="main-input"
        :customMessages="{ min_value: 'This field must be 0 or more', required: 'This field is required' }"
      >
        <b-form-group class="input-wrapper">
          <h5>Number of Operators</h5>
          <b-input-group>
            <b-input-group-prepend>
              <b-button variant="outline-info" @click="onDecrementClick">
                <i class="fas fa-minus" />
              </b-button>
            </b-input-group-prepend>
            <b-form-input
              v-model="operatorsNum"
              class="operators-input"
              type="number"
              min="0"
              required
              :state="getState(v)"
              size="lg"
              @change="onNumberInputChange(v.errors)"
            />
            <b-input-group-append>
              <b-button variant="outline-info" @click="onIncrementClick">
                <i class="fas fa-plus" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="getState(v)">
            {{ v.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>
      <button
        v-if="!autoplayAllowed"
        @click="initAudio"
        class="init-audio-button mr-5"
      >
        Enable Sound Notifications
      </button>
      <i class="fas fa-history history-button" @click="onHistoryClick" />
    </div>

    <div v-if="!loading">
      <div v-if="computedCards.length" class="mb-4">
        <hr />
        <h4 class="p-0 m-0 d-flex justify-content-center">New Collections</h4>
        <hr class="mb-4" />
        <div class="row ml-2 mr-2 justify-content-center">
          <collection-card
            class="m-2 collection-card"
            v-for="card in computedCards"
            :key="card.uuid"
            :object="card"
            :notifyIfOverdue="true"
            @onCardUpdate="onCardUpdate"
          />
        </div>
      </div>
      <div v-if="returnedCards.length">
        <hr />
        <h4 class="p-0 m-0 d-flex justify-content-center">Returned Collections</h4>
        <hr class="mb-4" />
        <div class="row ml-2 mr-2 justify-content-center">
          <collection-card
            class="m-2 collection-card"
            v-for="card in returnedCards"
            :key="card.uuid"
            :object="card"
            @onCardUpdate="onCardUpdate"
          />
        </div>
      </div>
    </div>

    <footer class="footer">
      Average collection time: {{ formattedAvgCollectionTime }} min
    </footer>
  </div>
</template>

<script>
import CollectionCard from '@/modules/Operator/CollectionCard'
import { mapActions, mapState } from 'pinia'
import { useOperatorStore } from '@/store/operator'
import { useSettingStore } from '@/store/settings'
import { useWorkingHoursStore } from '@/store/workingHours'

export default {
  name: 'Operator',
  components: { CollectionCard },
  data() {
    return {
      operatorsNum: 0,
      avgCollectionTime: 60,
      audio: null,
      audioInitialized: false,
      previousCardIds: new Set(),
      checkInterval: null,
      autoplayAllowed: false,
    }
  },
  computed: {
    ...mapState(useOperatorStore, ['prescriptionCards', 'prescriptionsHistory', 'loading']),
    ...mapState(useSettingStore, ['settings', 'settingsList']),
    ...mapState(useWorkingHoursStore, ['isWorkingHours']),
    computedCards() {
      return this.prescriptionCards
        .filter(pc => pc.returned_on === null)
        .slice(0, this.operatorsNum)
    },
    returnedCards() {
      return this.prescriptionCards.filter(pc => pc.returned_on !== null)
    },
    formattedAvgCollectionTime() {
      return Math.ceil(this.avgCollectionTime / 60)
    },
    incompleteCount() {
      return this.prescriptionCards.filter(pc =>
        [0, '0', 1, '1', 2, '2'].includes(pc.current_status)
      ).length
    },
    completedTodayCount() {
      return this.prescriptionsHistory.length
    }
  },
  watch: {
    isWorkingHours(newVal) {
      if (newVal && this.operatorsNum === 0) {
        this.operatorsNum = 1
        this.updateOperatorsSetting('number_of_operators', 1)
      } else if (!newVal && this.operatorsNum !== 0) {
        this.operatorsNum = 0
        this.updateOperatorsSetting('number_of_operators', 0)
      }
    },
    prescriptionCards: {
      handler(newCards) {
        const newIncomingCards = newCards.filter(card =>
          !this.previousCardIds.has(card.uuid) && card.current_status === 0
        )
        if (newIncomingCards.length > 0) {
          this.playNewCardSound()
          newIncomingCards.forEach(card => this.previousCardIds.add(card.uuid))
        }
      },
      deep: true,
      immediate: true,
    }
  },
  methods: {
    ...mapActions(useOperatorStore, [
      'getPrescriptions',
      'updatePrescription',
      'updatePrescriptionCards',
      'getPrescriptionsHistory',
      'updateHistoryCards'
    ]),
    ...mapActions(useSettingStore, ['getSettings', 'getSettingsList', 'updateSetting']),
    ...mapActions(useWorkingHoursStore, ['updateWorkingHours']),
    onIncrementClick() {
      this.operatorsNum++
      this.updateOperatorsSetting('number_of_operators', this.operatorsNum)
    },
    onDecrementClick() {
      if (this.operatorsNum > 0) {
        this.operatorsNum--
        this.updateOperatorsSetting('number_of_operators', this.operatorsNum)
      }
    },
    onNumberInputChange(errors) {
      if (!errors.length) {
        this.updateOperatorsSetting('number_of_operators', this.operatorsNum)
      }
    },
    updateOperatorsSetting(key, value) {
      const setting = this.settings.find(s => s.key === key)
      if (setting && Object.keys(setting).length) {
        this.updateSetting({ uuid: setting.uuid, data: { ...setting, value } })
      }
    },
    async onCardUpdate({ uuid, data }) {
      if (data.current_status === 3) {
        const { success } = await this.updatePrescription({ uuid, data })
        if (success) {
          this.$toast('success', 'Card Completed Successfully')
        } else {
          this.$toast('danger', 'Unable to complete card')
        }
      } else {
        await this.updatePrescription({ uuid, data })
      }
    },
    getState(validator) {
      if (validator.validated === true) {
        return validator.valid === true ? null : false
      }
      if (!validator.touched) {
        return null
      }
      return validator.valid === true ? null : false
    },
    onHistoryClick() {
      this.$router.push({ name: 'history' })
    },
    handleAvgCollectionTime(data) {
      if (data && data.average_collection_time > 0) {
        this.avgCollectionTime = data.average_collection_time
      }
    },
    testAutoplay() {
      const testAudio = new Audio('/static/sounds/notification.mp3')
      testAudio.play().then(() => {
        this.autoplayAllowed = true;
      }).catch(() => {
        this.autoplayAllowed = false;
      })
    },
    playNewCardSound() {
      if (!this.audio) return;
      this.audio.play().catch(() => {});
      this.audio.addEventListener('ended', () => this.audio.play(), { once: true });
    },
    initAudio() {
      if (!this.audioInitialized) {
        this.audio = new Audio('/static/sounds/notification.mp3')
        this.audio.preload = 'auto'
        this.audio.volume = 1.0
        this.audio.play().then(() => {
          this.audioInitialized = true
        }).catch(() => {})
      }
    },
    async checkWorkingHoursLoop() {
      await this.updateWorkingHours()
    }
  },
  async mounted() {
    await this.updateWorkingHours()
    this.checkInterval = setInterval(this.checkWorkingHoursLoop, 60 * 1000)
    this.prescriptionCards.forEach(card => this.previousCardIds.add(card.uuid))
    this.getSettings()
    await this.getSettingsList()
    this.operatorsNum = this.settingsList.number_of_operators || 0
    this.getPrescriptions()
    this.getPrescriptionsHistory()
    this.$ws.connect()
    this.$ws.listen('SEND_CARD_TO_OPERATORS', this.updatePrescriptionCards)
    this.$ws.listen('RETURN_CARD_TO_OPERATORS', this.updatePrescriptionCards)
    this.$ws.listen('AVG_COLLECTION_TIME', this.handleAvgCollectionTime)
    this.$ws.listen('SEND_CARD_TO_HISTORY', this.updateHistoryCards)
    this.initAudio();
    this.testAutoplay();
  },
  destroyed() {
    clearInterval(this.checkInterval)
    this.$ws.remove('SEND_CARD_TO_OPERATORS')
    this.$ws.remove('RETURN_CARD_TO_OPERATORS')
    this.$ws.remove('AVG_COLLECTION_TIME')
    this.$ws.remove('SEND_CARD_TO_HISTORY')
  }
}
</script>

<style lang="scss" scoped>
.invalid-feedback {
  font-size: 12px;
}
.operators-input {
  border-bottom-color: #3989c6;
  border-top-color: #3989c6;
}
.operators-input:focus,
.operators-input:active,
.btn:focus,
.btn:active {
  box-shadow: none !important;
}
.input-wrapper {
  min-width: 430px;
}
.collection-card {
  min-width: 430px;
}
.main-input {
  flex: 1;
  display: flex;
  justify-content: center;
  transform: translateX(9px);
}
.history-button {
  cursor: pointer;
  font-size: 36px;
  color: #ffc107;
}
.footer {
  background: #0a8544;
  color: white;
  font-size: calc(100% + 2vw);
  font-weight: bold;
  width: 100%;
  height: 6vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: 0;
}
.init-audio-button {
  margin-left: 20px;
  padding: 10px 20px;
  background-color: #0a8544;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
}
.init-audio-button:hover {
  background-color: #3271a8;
}
</style>
